export type SymbolSymmetry = "none" | "vertical" | "horizontal";
export type DirectionalBiasMode =
    | "none"
    | "horizontal"
    | "vertical"
    | "radial"
    | "swirl";

export interface SymbolGridParams {
    rows: number;
    cols: number;
    density: number;
    complexity: number;
    symmetry: SymbolSymmetry;
    branching: number;
    mergeBias: number;
    directionalBiasMode: DirectionalBiasMode;
    directionalBiasStrength: number;
    maxPathLength: number;
    pathGapCells: number;
    keepLargestComponent?: boolean;
}

export type BinaryGrid = number[][];

export function createEmptyGrid(rows: number, cols: number): BinaryGrid {
    return Array.from({ length: rows }, () => Array(cols).fill(0));
}

export function cloneGrid(grid: BinaryGrid): BinaryGrid {
    return grid.map((row) => [...row]);
}

export function countActiveCells(grid: BinaryGrid): number {
    let count = 0;
    for (const row of grid) {
        for (const cell of row) {
            if (cell > 0) count += 1;
        }
    }
    return count;
}

export function generateSymbolGrid(
    params: SymbolGridParams,
    rng: () => number,
): BinaryGrid {
    const rows = Math.max(4, Math.floor(params.rows));
    const cols = Math.max(4, Math.floor(params.cols));
    const grid = createEmptyGrid(rows, cols);

    const totalCells = rows * cols;
    const targetFill = Math.max(
        4,
        Math.floor((Math.max(0, Math.min(100, params.density)) / 100) * totalCells),
    );
    const stepCount = Math.max(
        targetFill * 2,
        Math.floor(targetFill * (1.5 + Math.max(0, Math.min(1, params.complexity)))),
    );

    const branching = Math.max(0, Math.min(1, params.branching));
    const mergeBias = Math.max(0, Math.min(1, params.mergeBias));
    const directionalBiasStrength = Math.max(
        0,
        Math.min(1, params.directionalBiasStrength),
    );
    const maxPathLength = Math.max(0, Math.floor(params.maxPathLength));
    const pathGapCells = Math.max(0, Math.floor(params.pathGapCells));
    const walkers: Array<{ r: number; c: number; age: number }> = [
        { r: Math.floor(rng() * rows), c: Math.floor(rng() * cols), age: 0 },
    ];
    const maxWalkers = Math.max(2, Math.floor(2 + branching * 8));

    for (let i = 0; i < stepCount; i += 1) {
        for (let w = walkers.length - 1; w >= 0; w -= 1) {
            const walker = walkers[w] as { r: number; c: number; age: number };
            grid[walker.r][walker.c] = 1;
            walker.age += 1;
            if (maxPathLength > 0 && walker.age >= maxPathLength) {
                walker.age = 0;
                const reset = pickDistantResetCell(grid, rng, pathGapCells);
                walker.r = reset[0];
                walker.c = reset[1];
                continue;
            }
            const [nr, nc] = chooseNextStep(
                grid,
                walker.r,
                walker.c,
                rng,
                mergeBias,
                params.directionalBiasMode,
                directionalBiasStrength,
            );
            walker.r = nr;
            walker.c = nc;

            if (
                walkers.length < maxWalkers &&
                rng() < 0.02 + branching * 0.16
            ) {
                const spawn = pickDistantResetCell(grid, rng, pathGapCells);
                walkers.push({ r: spawn[0], c: spawn[1], age: 0 });
            }
        }
    }

    while (countActiveCells(grid) < targetFill) {
        const rr = Math.floor(rng() * rows);
        const cc = Math.floor(rng() * cols);
        if (hasActiveNeighbor(grid, rr, cc) || rng() > 0.75) {
            grid[rr][cc] = 1;
        }
    }

    removeIsolatedSingles(grid);
    if (params.keepLargestComponent !== false) {
        keepLargestConnectedComponent(grid);
    }
    applySymmetry(grid, params.symmetry);
    removeIsolatedSingles(grid);
    return grid;
}

function pickResetCell(grid: BinaryGrid, rng: () => number): [number, number] {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const active: Array<[number, number]> = [];
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (grid[r][c] > 0) active.push([r, c]);
        }
    }
    if (active.length === 0) {
        return [Math.floor(rng() * rows), Math.floor(rng() * cols)];
    }
    return active[Math.floor(rng() * active.length)] as [number, number];
}

function pickDistantResetCell(
    grid: BinaryGrid,
    rng: () => number,
    gapCells: number,
): [number, number] {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const candidates: Array<[number, number]> = [];
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (grid[r][c] > 0) continue;
            if (hasNearbyActiveCell(grid, r, c, gapCells)) continue;
            candidates.push([r, c]);
        }
    }
    if (candidates.length > 0) {
        return candidates[Math.floor(rng() * candidates.length)] as [number, number];
    }
    return pickResetCell(grid, rng);
}

function hasNearbyActiveCell(
    grid: BinaryGrid,
    r: number,
    c: number,
    gapCells: number,
): boolean {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const radius = Math.max(1, gapCells);
    for (let rr = r - radius; rr <= r + radius; rr += 1) {
        for (let cc = c - radius; cc <= c + radius; cc += 1) {
            if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) continue;
            if (grid[rr][cc] > 0) return true;
        }
    }
    return false;
}

function chooseNextStep(
    grid: BinaryGrid,
    r: number,
    c: number,
    rng: () => number,
    mergeBias: number,
    directionalBiasMode: DirectionalBiasMode,
    directionalBiasStrength: number,
): [number, number] {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const candidates: Array<[number, number]> = [
        [Math.max(0, r - 1), c],
        [Math.min(rows - 1, r + 1), c],
        [r, Math.max(0, c - 1)],
        [r, Math.min(cols - 1, c + 1)],
    ];
    if (rng() >= mergeBias) {
        return chooseBiasedCandidate(
            grid,
            candidates,
            r,
            c,
            directionalBiasMode,
            directionalBiasStrength,
            rng,
        );
    }

    let best = candidates[0] as [number, number];
    let bestScore = Number.NEGATIVE_INFINITY;
    for (const [nr, nc] of candidates) {
        const score =
            activeNeighborsCount(grid, nr, nc) +
            directionalScore(
                grid,
                r,
                c,
                nr,
                nc,
                directionalBiasMode,
                directionalBiasStrength,
            );
        if (score > bestScore) {
            bestScore = score;
            best = [nr, nc];
        }
    }
    return best;
}

function chooseBiasedCandidate(
    grid: BinaryGrid,
    candidates: Array<[number, number]>,
    r: number,
    c: number,
    mode: DirectionalBiasMode,
    strength: number,
    rng: () => number,
): [number, number] {
    if (mode === "none" || strength <= 0) {
        return candidates[Math.floor(rng() * candidates.length)] as [number, number];
    }
    const weighted = candidates.map((cand) => {
        const [nr, nc] = cand;
        const s = directionalScore(grid, r, c, nr, nc, mode, strength);
        return {
            cand,
            weight: Math.max(0.01, 1 + s),
        };
    });
    const total = weighted.reduce((sum, w) => sum + w.weight, 0);
    let pick = rng() * total;
    for (const w of weighted) {
        pick -= w.weight;
        if (pick <= 0) return w.cand;
    }
    return weighted[weighted.length - 1]?.cand ?? candidates[0]!;
}

function directionalScore(
    grid: BinaryGrid,
    r: number,
    c: number,
    nr: number,
    nc: number,
    mode: DirectionalBiasMode,
    strength: number,
): number {
    if (mode === "none" || strength <= 0) return 0;
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const dr = nr - r;
    const dc = nc - c;
    const cx = (cols - 1) / 2;
    const cy = (rows - 1) / 2;
    const vx = c - cx;
    const vy = r - cy;

    if (mode === "horizontal") {
        return Math.abs(dc) > 0 ? 1.6 * strength : -0.8 * strength;
    }
    if (mode === "vertical") {
        return Math.abs(dr) > 0 ? 1.6 * strength : -0.8 * strength;
    }
    if (mode === "radial") {
        const dot = vx * dc + vy * dr;
        return (dot >= 0 ? 1.2 : -0.6) * strength;
    }
    if (mode === "swirl") {
        const tx = -vy;
        const ty = vx;
        const dot = tx * dc + ty * dr;
        return (dot >= 0 ? 1.3 : -0.65) * strength;
    }
    return 0;
}

function activeNeighborsCount(grid: BinaryGrid, r: number, c: number): number {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    let score = 0;
    const neighbors = [
        [r - 1, c],
        [r + 1, c],
        [r, c - 1],
        [r, c + 1],
    ];
    for (const [nr, nc] of neighbors) {
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        if (grid[nr][nc] > 0) score += 1;
    }
    return score;
}

function hasActiveNeighbor(grid: BinaryGrid, r: number, c: number): boolean {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const neighbors = [
        [r - 1, c],
        [r + 1, c],
        [r, c - 1],
        [r, c + 1],
    ];
    for (const [nr, nc] of neighbors) {
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        if (grid[nr][nc] > 0) return true;
    }
    return false;
}

function removeIsolatedSingles(grid: BinaryGrid): void {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const next = cloneGrid(grid);
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (grid[r][c] === 0) continue;
            if (!hasActiveNeighbor(grid, r, c)) {
                next[r][c] = 0;
            }
        }
    }
    for (let r = 0; r < rows; r += 1) {
        grid[r] = next[r];
    }
}

function keepLargestConnectedComponent(grid: BinaryGrid): void {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    const visited = createEmptyGrid(rows, cols);
    let best: Array<[number, number]> = [];

    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (grid[r][c] === 0 || visited[r][c] > 0) continue;
            const queue: Array<[number, number]> = [[r, c]];
            const cells: Array<[number, number]> = [];
            visited[r][c] = 1;
            while (queue.length > 0) {
                const [cr, cc] = queue.shift() as [number, number];
                cells.push([cr, cc]);
                const neighbors = [
                    [cr - 1, cc],
                    [cr + 1, cc],
                    [cr, cc - 1],
                    [cr, cc + 1],
                ];
                for (const [nr, nc] of neighbors) {
                    if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
                    if (visited[nr][nc] > 0 || grid[nr][nc] === 0) continue;
                    visited[nr][nc] = 1;
                    queue.push([nr, nc]);
                }
            }
            if (cells.length > best.length) best = cells;
        }
    }

    const keep = new Set(best.map(([r, c]) => `${r}:${c}`));
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (!keep.has(`${r}:${c}`)) grid[r][c] = 0;
        }
    }
}

function applySymmetry(grid: BinaryGrid, symmetry: SymbolSymmetry): void {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    if (symmetry === "vertical") {
        for (let r = 0; r < rows; r += 1) {
            for (let c = 0; c < Math.floor(cols / 2); c += 1) {
                const mirror = cols - 1 - c;
                if (grid[r][c] > 0 || grid[r][mirror] > 0) {
                    grid[r][c] = 1;
                    grid[r][mirror] = 1;
                }
            }
        }
    } else if (symmetry === "horizontal") {
        for (let r = 0; r < Math.floor(rows / 2); r += 1) {
            const mirror = rows - 1 - r;
            for (let c = 0; c < cols; c += 1) {
                if (grid[r][c] > 0 || grid[mirror][c] > 0) {
                    grid[r][c] = 1;
                    grid[mirror][c] = 1;
                }
            }
        }
    }
}
